import React, { useState, useEffect, useCallback } from 'react';
import {
  ChartBarIcon,
  DocumentArrowDownIcon,
  ArrowPathIcon,
  EnvelopeIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  CloudIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
} from '@heroicons/react/24/outline';
import {
  ChartBarIcon as ChartBarIconSolid,
  EnvelopeIcon as EnvelopeIconSolid,
  UserGroupIcon as UserGroupIconSolid,
  ShieldCheckIcon as ShieldCheckIconSolid,
  CloudIcon as CloudIconSolid
} from '@heroicons/react/24/solid';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';

import axiosInstance from '../api/axiosInstance';

const TIME_RANGES = [
  { value: '24h', label: '24h' },
  { value: '7d', label: '7d' },
  { value: '30d', label: '30d' },
  { value: '90d', label: '90d' },
];

const formatNumber = (num) => {
  if (!num) return '0';
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toString();
};

const formatBytes = (bytes) => {
  if (!bytes) return '0 B';
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return (bytes / Math.pow(1024, i)).toFixed(1) + ' ' + sizes[i];
};

// Compact stat card
const StatCard = ({ icon, iconBg, label, value, sub, growth }) => (
  <div className="bg-white dark:bg-[#1a1a1a] rounded-lg border border-gray-200 dark:border-gray-800 p-3">
    <div className="flex items-center justify-between mb-1.5">
      <div className={`w-7 h-7 rounded-md flex items-center justify-center ${iconBg}`}>
        {icon}
      </div>
      {growth !== undefined && growth !== null && (
        <span className={`text-[11px] font-medium flex items-center gap-0.5 ${growth >= 0 ? 'text-green-600' : 'text-red-500'}`}>
          {growth >= 0 ? <ArrowTrendingUpIcon className="w-3 h-3" /> : <ArrowTrendingDownIcon className="w-3 h-3" />}
          {Math.abs(growth)}%
        </span>
      )}
    </div>
    <p className="text-lg font-bold text-gray-900 dark:text-white tabular-nums">{value}</p>
    <p className="text-[11px] text-gray-500 dark:text-gray-400">{label}</p>
    {sub && <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">{sub}</p>}
  </div>
);

// Chart wrapper
const ChartCard = ({ title, children, empty }) => (
  <div className="bg-white dark:bg-[#1a1a1a] rounded-lg border border-gray-200 dark:border-gray-800">
    <div className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-800">
      <h3 className="text-xs font-semibold text-gray-900 dark:text-white">{title}</h3>
    </div>
    <div className="p-4">
      {empty ? (
        <div className="h-48 flex items-center justify-center">
          <p className="text-xs text-gray-400 dark:text-gray-500">No data available for this period</p>
        </div>
      ) : (
        <div className="h-48">{children}</div>
      )}
    </div>
  </div>
);

// Simple data table
const DataTable = ({ columns, rows }) => (
  <div className="bg-white dark:bg-[#1a1a1a] rounded-lg border border-gray-200 dark:border-gray-800 overflow-hidden">
    <table className="w-full">
      <thead>
        <tr className="border-b border-gray-100 dark:border-gray-800">
          {columns.map((col, i) => (
            <th key={i} className="px-3 py-2 text-left text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {col}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
        {rows.length > 0 ? rows : (
          <tr>
            <td colSpan={columns.length} className="px-3 py-6 text-center text-xs text-gray-400">
              No data available
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

const chartTooltipStyle = {
  contentStyle: {
    background: '#1a1a1a',
    border: '1px solid #333',
    borderRadius: '6px',
    fontSize: '11px',
    padding: '6px 10px',
  },
  itemStyle: { color: '#ccc', fontSize: '11px' },
  labelStyle: { color: '#999', fontSize: '10px' },
};

const ModernReports = ({ showNotification }) => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedTimeRange, setSelectedTimeRange] = useState('30d');
  const [reportData, setReportData] = useState(null);
  const [stats, setStats] = useState({});

  const fetchReportData = useCallback(async () => {
    try {
      setLoading(true);
      const endpoint = `/api/business/reports/${activeTab}`;
      try {
        const response = await axiosInstance.get(endpoint, {
          params: { timeRange: selectedTimeRange }
        });
        if (response.data?.success) {
          setReportData(response.data.reports || {});
          setStats(response.data.stats || {});
        }
      } catch {
        setReportData({});
        setStats({});
      }
    } catch {
      setReportData({});
      setStats({});
    } finally {
      setLoading(false);
    }
  }, [selectedTimeRange, activeTab]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  const handleExport = async (format) => {
    try {
      const response = await axiosInstance.post(`/api/business/reports/export/${activeTab}`, {
        timeRange: selectedTimeRange,
        format
      });
      if (response.data?.success) {
        const blob = new Blob([response.data.content], { type: response.data.mimeType });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${activeTab}-report-${new Date().toISOString().split('T')[0]}.${format}`;
        a.click();
        window.URL.revokeObjectURL(url);
        showNotification?.(`Exported ${format.toUpperCase()} successfully`, 'success');
      }
    } catch {
      showNotification?.('Export not available yet', 'error');
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: <ChartBarIcon className="w-3.5 h-3.5" /> },
    { id: 'email', label: 'Email', icon: <EnvelopeIcon className="w-3.5 h-3.5" /> },
    { id: 'users', label: 'Users', icon: <UserGroupIcon className="w-3.5 h-3.5" /> },
    { id: 'security', label: 'Security', icon: <ShieldCheckIcon className="w-3.5 h-3.5" /> },
    { id: 'storage', label: 'Storage', icon: <CloudIcon className="w-3.5 h-3.5" /> },
  ];

  const renderOverview = () => (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          icon={<EnvelopeIconSolid className="w-3.5 h-3.5 text-blue-600" />}
          iconBg="bg-blue-100 dark:bg-blue-900/30"
          label="Total Emails"
          value={formatNumber(stats.totalEmails)}
          growth={stats.emailGrowth}
        />
        <StatCard
          icon={<UserGroupIconSolid className="w-3.5 h-3.5 text-green-600" />}
          iconBg="bg-green-100 dark:bg-green-900/30"
          label="Active Users"
          value={formatNumber(stats.activeUsers)}
          growth={stats.userGrowth}
        />
        <StatCard
          icon={<CloudIconSolid className="w-3.5 h-3.5 text-purple-600" />}
          iconBg="bg-purple-100 dark:bg-purple-900/30"
          label="Storage Used"
          value={stats.storageUsed ? `${stats.storageUsed}GB` : '0 GB'}
          growth={stats.storageGrowth}
        />
        <StatCard
          icon={<ShieldCheckIconSolid className="w-3.5 h-3.5 text-red-600" />}
          iconBg="bg-red-100 dark:bg-red-900/30"
          label="Security Events"
          value={formatNumber(stats.securityEvents)}
          growth={stats.securityGrowth}
        />
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-4">
        <ChartCard title="Email Activity" empty={!reportData?.emailActivity?.length}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={reportData?.emailActivity || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" tickFormatter={(d) => new Date(d).toLocaleDateString('en', { month: 'short', day: 'numeric' })} tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <RechartsTooltip {...chartTooltipStyle} />
              <Area type="monotone" dataKey="sent" stroke="#F97316" fill="#F9731620" strokeWidth={2} name="Sent" />
              <Area type="monotone" dataKey="received" stroke="#3B82F6" fill="#3B82F620" strokeWidth={2} name="Received" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="User Activity" empty={!reportData?.userActivity?.length}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={reportData?.userActivity || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" tickFormatter={(d) => new Date(d).toLocaleDateString('en', { month: 'short', day: 'numeric' })} tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <RechartsTooltip {...chartTooltipStyle} />
              <Line type="monotone" dataKey="activeUsers" stroke="#10B981" strokeWidth={2} dot={false} name="Active Users" />
              <Line type="monotone" dataKey="sessions" stroke="#8B5CF6" strokeWidth={2} dot={false} name="Sessions" />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Top Domains */}
      <DataTable
        columns={['Domain', 'Emails', 'Users']}
        rows={(reportData?.topDomains || []).map((d, i) => (
          <tr key={i} className="hover:bg-gray-50 dark:hover:bg-[#222222]">
            <td className="px-3 py-2 text-xs font-medium text-gray-900 dark:text-white">{d.domain}</td>
            <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-300 tabular-nums">{formatNumber(d.emails)}</td>
            <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-300 tabular-nums">{d.users}</td>
          </tr>
        ))}
      />
    </div>
  );

  const renderEmail = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={<EnvelopeIconSolid className="w-3.5 h-3.5 text-blue-600" />} iconBg="bg-blue-100 dark:bg-blue-900/30" label="Total Sent" value={formatNumber(stats.totalSent)} growth={stats.sentGrowth} />
        <StatCard icon={<EnvelopeIconSolid className="w-3.5 h-3.5 text-green-600" />} iconBg="bg-green-100 dark:bg-green-900/30" label="Total Received" value={formatNumber(stats.totalReceived)} growth={stats.receivedGrowth} />
        <StatCard icon={<ArrowTrendingUpIcon className="w-3.5 h-3.5 text-orange-600" />} iconBg="bg-orange-100 dark:bg-orange-900/30" label="Delivery Rate" value={stats.deliveryRate ? `${stats.deliveryRate}%` : 'N/A'} />
        <StatCard icon={<ShieldCheckIconSolid className="w-3.5 h-3.5 text-red-600" />} iconBg="bg-red-100 dark:bg-red-900/30" label="Spam Blocked" value={formatNumber(stats.spamBlocked)} />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <ChartCard title="Email Volume" empty={!reportData?.emailActivity?.length}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={reportData?.emailActivity || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" tickFormatter={(d) => new Date(d).toLocaleDateString('en', { month: 'short', day: 'numeric' })} tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <RechartsTooltip {...chartTooltipStyle} />
              <Bar dataKey="sent" fill="#F97316" radius={[2, 2, 0, 0]} name="Sent" />
              <Bar dataKey="received" fill="#3B82F6" radius={[2, 2, 0, 0]} name="Received" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Delivery Metrics" empty={!reportData?.deliveryMetrics?.length}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={reportData?.deliveryMetrics || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" tickFormatter={(d) => new Date(d).toLocaleDateString('en', { month: 'short', day: 'numeric' })} tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <RechartsTooltip {...chartTooltipStyle} />
              <Line type="monotone" dataKey="deliveryRate" stroke="#10B981" strokeWidth={2} dot={false} name="Delivery %" />
              <Line type="monotone" dataKey="bounceRate" stroke="#EF4444" strokeWidth={2} dot={false} name="Bounce %" />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <DataTable
        columns={['Sender', 'Sent', 'Delivered', 'Bounced']}
        rows={(reportData?.topSenders || []).map((s, i) => (
          <tr key={i} className="hover:bg-gray-50 dark:hover:bg-[#222222]">
            <td className="px-3 py-2 text-xs font-medium text-gray-900 dark:text-white">{s.email}</td>
            <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-300 tabular-nums">{formatNumber(s.sent)}</td>
            <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-300 tabular-nums">{formatNumber(s.delivered)}</td>
            <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-300 tabular-nums">{formatNumber(s.bounced)}</td>
          </tr>
        ))}
      />
    </div>
  );

  const renderUsers = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={<UserGroupIconSolid className="w-3.5 h-3.5 text-blue-600" />} iconBg="bg-blue-100 dark:bg-blue-900/30" label="Total Users" value={formatNumber(stats.totalUsers)} />
        <StatCard icon={<UserGroupIconSolid className="w-3.5 h-3.5 text-green-600" />} iconBg="bg-green-100 dark:bg-green-900/30" label="Active (30d)" value={formatNumber(stats.activeUsers)} growth={stats.userGrowth} />
        <StatCard icon={<ArrowTrendingUpIcon className="w-3.5 h-3.5 text-orange-600" />} iconBg="bg-orange-100 dark:bg-orange-900/30" label="New Users" value={formatNumber(stats.newUsers)} growth={stats.newUserGrowth} />
        <StatCard icon={<ChartBarIconSolid className="w-3.5 h-3.5 text-purple-600" />} iconBg="bg-purple-100 dark:bg-purple-900/30" label="Avg Session" value={stats.avgSessionTime ? `${stats.avgSessionTime}m` : 'N/A'} />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <ChartCard title="Active Users Over Time" empty={!reportData?.userActivity?.length}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={reportData?.userActivity || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" tickFormatter={(d) => new Date(d).toLocaleDateString('en', { month: 'short', day: 'numeric' })} tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <RechartsTooltip {...chartTooltipStyle} />
              <Area type="monotone" dataKey="activeUsers" stroke="#3B82F6" fill="#3B82F620" strokeWidth={2} name="Active Users" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="New Sign-ups" empty={!reportData?.signups?.length}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={reportData?.signups || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" tickFormatter={(d) => new Date(d).toLocaleDateString('en', { month: 'short', day: 'numeric' })} tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <RechartsTooltip {...chartTooltipStyle} />
              <Bar dataKey="count" fill="#10B981" radius={[2, 2, 0, 0]} name="New Users" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <DataTable
        columns={['User', 'Last Active', 'Emails', 'Storage']}
        rows={(reportData?.topUsers || []).map((u, i) => (
          <tr key={i} className="hover:bg-gray-50 dark:hover:bg-[#222222]">
            <td className="px-3 py-2 text-xs font-medium text-gray-900 dark:text-white">{u.email || u.name}</td>
            <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-300">{u.lastActive ? new Date(u.lastActive).toLocaleDateString() : 'N/A'}</td>
            <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-300 tabular-nums">{formatNumber(u.emails)}</td>
            <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-300 tabular-nums">{u.storage ? formatBytes(u.storage) : 'N/A'}</td>
          </tr>
        ))}
      />
    </div>
  );

  const renderSecurity = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={<ShieldCheckIconSolid className="w-3.5 h-3.5 text-blue-600" />} iconBg="bg-blue-100 dark:bg-blue-900/30" label="Events Logged" value={formatNumber(stats.securityEvents)} growth={stats.eventGrowth} />
        <StatCard icon={<ShieldCheckIconSolid className="w-3.5 h-3.5 text-red-600" />} iconBg="bg-red-100 dark:bg-red-900/30" label="Threats Blocked" value={formatNumber(stats.threatsBlocked)} />
        <StatCard icon={<ShieldCheckIconSolid className="w-3.5 h-3.5 text-orange-600" />} iconBg="bg-orange-100 dark:bg-orange-900/30" label="Failed Logins" value={formatNumber(stats.failedLogins)} />
        <StatCard icon={<ShieldCheckIconSolid className="w-3.5 h-3.5 text-green-600" />} iconBg="bg-green-100 dark:bg-green-900/30" label="Risk Score" value={stats.riskScore !== undefined ? `${stats.riskScore}/100` : 'N/A'} />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <ChartCard title="Security Events" empty={!reportData?.securityEvents?.length}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={reportData?.securityEvents || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" tickFormatter={(d) => new Date(d).toLocaleDateString('en', { month: 'short', day: 'numeric' })} tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <RechartsTooltip {...chartTooltipStyle} />
              <Bar dataKey="events" fill="#EF4444" radius={[2, 2, 0, 0]} name="Events" />
              <Bar dataKey="blocked" fill="#10B981" radius={[2, 2, 0, 0]} name="Blocked" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Login Activity" empty={!reportData?.loginActivity?.length}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={reportData?.loginActivity || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" tickFormatter={(d) => new Date(d).toLocaleDateString('en', { month: 'short', day: 'numeric' })} tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <RechartsTooltip {...chartTooltipStyle} />
              <Line type="monotone" dataKey="successful" stroke="#10B981" strokeWidth={2} dot={false} name="Successful" />
              <Line type="monotone" dataKey="failed" stroke="#EF4444" strokeWidth={2} dot={false} name="Failed" />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <DataTable
        columns={['Event', 'Type', 'Source', 'Time']}
        rows={(reportData?.recentEvents || []).slice(0, 10).map((e, i) => (
          <tr key={i} className="hover:bg-gray-50 dark:hover:bg-[#222222]">
            <td className="px-3 py-2 text-xs font-medium text-gray-900 dark:text-white">{e.description || e.event}</td>
            <td className="px-3 py-2">
              <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${
                e.severity === 'high' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                e.severity === 'medium' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
              }`}>
                {e.type || e.severity || 'info'}
              </span>
            </td>
            <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-300">{e.source || e.ip || 'N/A'}</td>
            <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">{e.timestamp ? new Date(e.timestamp).toLocaleString() : 'N/A'}</td>
          </tr>
        ))}
      />
    </div>
  );

  const renderStorage = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={<CloudIconSolid className="w-3.5 h-3.5 text-blue-600" />} iconBg="bg-blue-100 dark:bg-blue-900/30" label="Total Used" value={stats.storageUsed ? `${stats.storageUsed} GB` : '0 GB'} growth={stats.storageGrowth} />
        <StatCard icon={<CloudIconSolid className="w-3.5 h-3.5 text-green-600" />} iconBg="bg-green-100 dark:bg-green-900/30" label="Total Quota" value={stats.storageTotal ? `${stats.storageTotal} GB` : 'N/A'} />
        <StatCard icon={<EnvelopeIconSolid className="w-3.5 h-3.5 text-orange-600" />} iconBg="bg-orange-100 dark:bg-orange-900/30" label="Email Storage" value={stats.emailStorage ? `${stats.emailStorage} GB` : '0 GB'} />
        <StatCard icon={<CloudIconSolid className="w-3.5 h-3.5 text-purple-600" />} iconBg="bg-purple-100 dark:bg-purple-900/30" label="File Storage" value={stats.fileStorage ? `${stats.fileStorage} GB` : '0 GB'} />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <ChartCard title="Storage Growth" empty={!reportData?.storageGrowth?.length}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={reportData?.storageGrowth || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" tickFormatter={(d) => new Date(d).toLocaleDateString('en', { month: 'short', day: 'numeric' })} tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <RechartsTooltip {...chartTooltipStyle} />
              <Area type="monotone" dataKey="used" stroke="#8B5CF6" fill="#8B5CF620" strokeWidth={2} name="Used (GB)" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Usage by Type" empty={!reportData?.usageByType?.length}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={reportData?.usageByType || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="type" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <RechartsTooltip {...chartTooltipStyle} />
              <Bar dataKey="size" fill="#F97316" radius={[2, 2, 0, 0]} name="Size (GB)" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <DataTable
        columns={['User', 'Email', 'Files', 'Total']}
        rows={(reportData?.userStorage || []).map((u, i) => (
          <tr key={i} className="hover:bg-gray-50 dark:hover:bg-[#222222]">
            <td className="px-3 py-2 text-xs font-medium text-gray-900 dark:text-white">{u.email || u.name}</td>
            <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-300 tabular-nums">{u.emailStorage ? formatBytes(u.emailStorage) : 'N/A'}</td>
            <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-300 tabular-nums">{u.fileStorage ? formatBytes(u.fileStorage) : 'N/A'}</td>
            <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-300 tabular-nums">{u.totalStorage ? formatBytes(u.totalStorage) : 'N/A'}</td>
          </tr>
        ))}
      />
    </div>
  );

  const tabContent = {
    overview: renderOverview,
    email: renderEmail,
    users: renderUsers,
    security: renderSecurity,
    storage: renderStorage,
  };

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-[#111111] overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 bg-white dark:bg-[#1a1a1a] border-b border-gray-200 dark:border-gray-800">
        <div className="px-6 pt-2">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <ChartBarIconSolid className="w-4 h-4 text-orange-600 dark:text-orange-400" />
              <h1 className="text-sm font-semibold text-gray-900 dark:text-white">Reports</h1>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center border border-gray-300 dark:border-gray-700 rounded-md overflow-hidden">
                {TIME_RANGES.map((range) => (
                  <button
                    key={range.value}
                    onClick={() => setSelectedTimeRange(range.value)}
                    className={`px-2.5 py-0.5 text-[11px] font-medium transition-colors ${
                      selectedTimeRange === range.value
                        ? 'bg-orange-500 text-white'
                        : 'bg-white dark:bg-[#222222] text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#2a2a2a]'
                    }`}
                  >
                    {range.label}
                  </button>
                ))}
              </div>
              <button
                onClick={() => handleExport('csv')}
                className="p-1.5 rounded-md bg-gray-100 dark:bg-[#222222] hover:bg-gray-200 dark:hover:bg-[#2a2a2a] transition-colors"
                title="Export CSV"
              >
                <DocumentArrowDownIcon className="w-3.5 h-3.5 text-gray-600 dark:text-gray-300" />
              </button>
              <button
                onClick={fetchReportData}
                disabled={loading}
                className="p-1.5 rounded-md bg-gray-100 dark:bg-[#222222] hover:bg-gray-200 dark:hover:bg-[#2a2a2a] transition-colors disabled:opacity-50"
              >
                <ArrowPathIcon className={`w-3.5 h-3.5 text-gray-600 dark:text-gray-300 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-0">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-orange-500 text-orange-600 dark:text-orange-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500"></div>
            </div>
          ) : (
            tabContent[activeTab]?.()
          )}
        </div>
      </div>
    </div>
  );
};

export default ModernReports;
